import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectScenario, selectSortedChains } from '../scenarioSlice';
import { OperationChain } from '@/types';
import { selectAllPoints, selectTsps } from '../../map/slice/mapSelectors';
import * as pathSlice from '../../paths/pathsSlice';
import { useTspFilter } from '../../helpers/hooks';
import { useOptimizationPaths } from '../../hooks/useOptimizationPaths';
import { useGetTSPsQuery } from '../../services/edgeApi';
import {
  copyChainToClipboard,
  getUniqueChainTsps,
} from '../../utils/chainUtils';
import toast from 'react-hot-toast';
import {
  OptimizationChainsTableRow,
  OPTIMIZATION_CHAINS_TABLE_HEADERS,
} from './OptimizationChainsTableRow';
import { OptimizationChainOptions } from './OptimizationChainOptions';

export type OptimizationChainsTableProps = {};

export const OptimizationChainsTable: React.FC<
  OptimizationChainsTableProps
> = () => {
  useOptimizationPaths();
  useGetTSPsQuery();
  const dispatch = useDispatch();
  const setTspFilter = useTspFilter();
  const tsps = useSelector(selectTsps);
  const selectedPathId = useSelector(pathSlice.selectActivePathId);
  const pathsVisibility = useSelector(pathSlice.selectPathsVisibility);
  const points = useSelector(selectAllPoints);
  const chains = useSelector(selectSortedChains);
  const scenario = useSelector(selectScenario);

  const zoomToPath = useCallback(
    (pathId: number) => {
      dispatch(pathSlice.zoomToPath(pathId));
    },
    [dispatch],
  );

  const toggleVisibility = useCallback(
    (pathId: number) => {
      dispatch(pathSlice.toggleVisibility(pathId));
    },
    [dispatch],
  );

  const filterTsps = useCallback(
    (chain: OperationChain) => {
      const tsps = getUniqueChainTsps(scenario, chain);
      if (tsps.length > 0) {
        setTspFilter(tsps);
        zoomToPath(chain.id);
        selectChain(chain.id);
      }
    },
    [setTspFilter, scenario],
  );

  const selectChain = useCallback(
    (chainId: number) => {
      dispatch(pathSlice.pathSelected({ selectedPathId: chainId }));
    },
    [dispatch],
  );

  const copyToClipboard = useCallback(
    (chain: OperationChain) => {
      copyChainToClipboard(scenario, chain);
      toast.success('Chain copied to clipboard');
    },
    [scenario],
  );

  return (
    <table
      style={{
        position: 'relative',
      }}
      className="table"
    >
      <thead>
        <tr>
          <th colSpan={OPTIMIZATION_CHAINS_TABLE_HEADERS.length}>
            <OptimizationChainOptions scenario={scenario} />
          </th>
        </tr>
        <tr
          style={{
            position: 'sticky',
            top: 0,
            backgroundColor: 'var(--bs-body-bg)',
            zIndex: 2,
          }}
        >
          {OPTIMIZATION_CHAINS_TABLE_HEADERS.map((header) => (
            <th key={header}>{header}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {chains.map((chain, index) => (
          <OptimizationChainsTableRow
            key={chain.id}
            tsps={tsps}
            scenario={scenario}
            chain={chain}
            points={points}
            chainNumber={index + 1}
            isSelected={selectedPathId === chain.id}
            isVisible={pathsVisibility[chain.id.toString()] === true}
            onZoomIn={zoomToPath}
            onToggleVisibility={toggleVisibility}
            onFilterTsps={filterTsps}
            onSelectChain={selectChain}
            onCopyToClipboard={copyToClipboard}
          />
        ))}
      </tbody>
    </table>
  );
};
